import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlay, FaPause, FaArrowLeft, FaForward, FaBackward, FaSync } from 'react-icons/fa';
import axios from 'axios';
import { QUIZMASTER_API } from '../config';
import FloatingChat from './FloatingChat';
import '../styles/styles.css';

const VideoPage = ({ accessToken }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectedVideo = location.state?.selectedVideo;
    const document = location.state?.selectedDocument;

    const [videoUrl, setVideoUrl] = useState(null);
    const [status, setStatus] = useState(selectedVideo?.status);
    const [loading, setLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);

    const fetchVideoUrl = useCallback(async () => {
        if (!selectedVideo?.video_id) return;
        try {
            const response = await axios.get(
                `${QUIZMASTER_API}/videos/get-video-url/?video_id=${selectedVideo.video_id}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            // FIX: Use functional update to check videoUrl without making it a dependency
            setVideoUrl(prevUrl => {
                const newUrl = response.data.video_url;
                if (newUrl && newUrl !== prevUrl) {
                    // Only load the video if the URL is brand new to prevent resetting play position
                    if (videoRef.current) {
                        setTimeout(() => videoRef.current.load(), 0);
                    }
                    return newUrl;
                }
                return prevUrl;
            });

            setStatus(response.data.status);
        } catch (err) {
            console.error("Error fetching video");
        } finally {
            setLoading(false);
        }
    }, [selectedVideo, accessToken]); // Removed videoUrl dependency to stop the loop

    useEffect(() => {
        fetchVideoUrl();
    }, [fetchVideoUrl]);

    useEffect(() => {
        let interval;
        if (status !== 'completed') {
            interval = setInterval(() => {
                fetchVideoUrl();
            }, 10000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [status, fetchVideoUrl]);

    const skip = (seconds) => {
        if (videoRef.current) videoRef.current.currentTime += seconds;
    };

    return (
        <div className="scroll-container">
            <div className="podcast-page-container">
                {/* FIX: Corrected Header Styling to match App/Podcast layout */}
                <div className="podcast-header">
                    <div className="podcast-list-header__titles">
                        <button className="back-button" onClick={() => navigate('/video-list', { state: { selectedDocument: document } })}>
                            <FaArrowLeft /> Back to List
                        </button>
                        <h1 className="podcast-title">{selectedVideo?.title}</h1>
                    </div>
                </div>

                <div className="podcast-main-content">
                    {loading && !videoUrl ? (
                        <div className="status-container"><FaSync className="spinner" /></div>
                    ) : videoUrl ? (
                        <div className="video-player-section">
                            <div className="video-player-wrapper">
                                <video
                                    ref={videoRef}
                                    controls
                                    width="100%"
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                >
                                    <source src={videoUrl} type="video/mp4" />
                                </video>
                            </div>
                            <div className="podcast-controls" style={{ justifyContent: 'center', marginTop: '20px' }}>
                                <button className="control-button" onClick={() => skip(-10)}><FaBackward /></button>
                                <button className="play-button" onClick={() => isPlaying ? videoRef.current.pause() : videoRef.current.play()}>
                                    {isPlaying ? <FaPause /> : <FaPlay />}
                                </button>
                                <button className="control-button" onClick={() => skip(10)}><FaForward /></button>
                            </div>
                        </div>
                    ) : (
                        <div className="status-container">
                            <p>{status === 'processing' ? "Video is being rendered. Check back in a few minutes." : "Video not available."}</p>
                        </div>
                    )}
                </div>
                {document && <FloatingChat documentId={document.id} filename={document.filename} accessToken={accessToken} />}
            </div>
        </div>
    );
};

export default VideoPage;